import { FieldBlock, FieldType, TextStyle } from '@coast/schemas';
import { capitalize } from 'lodash';
import React, { ReactElement, useContext, useMemo } from 'react';
import { CardChannelContext } from '@shared/components/blocks/contexts/channel';
import { MBFieldBlock, defaultFieldPresenter } from '@shared/components/blocks/lib/field';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { User } from '@shared/types/user';
import { useChannelUsers } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { useResolveAssignees } from './hooks';
import { MBAssigneeUserIcon } from './userIcon';

interface Props {
  value: number[];
  block: FieldBlock;
  onPress?(): void;
}

// TODO(fant): i18n
export function assigneesToString(users: User[]): string {
  if (!users.length) {
    return i18n('card.blocks.assignee.unassigned');
  }
  const otherUsers = users.filter(u => !u.isMe);
  const me = users.find(u => u.isMe);

  if (!!me) {
    if (users.length === 1) {
      return `${capitalize(me.username)} (Me)`;
    }
    if (users.length === 2) {
      return `${capitalize(me.username)} (Me) and ${capitalize(otherUsers[0].username)}`;
    }

    return `${capitalize(me.username)} (Me) and +${otherUsers.length} others`;
  }

  if (users.length === 1) {
    return `${capitalize(users[0].username)}`;
  }
  if (users.length === 2) {
    return `${capitalize(users[0].username)} and ${capitalize(users[1].username)}`;
  }

  return `${capitalize(users[0].username)} and +${users.length - 1} others`;
}

export function fieldPresenter(
  value: MBAPI.ShortUser[],
  fieldType: FieldType,
  textStyle: TextStyle,
  textColor: string,
): ReactElement {
  return defaultFieldPresenter(assigneesToString(value), fieldType, textStyle, textColor);
}

export function MBFieldAssigneeBlock({ value, block, onPress }: Props) {
  const channel = useContext(CardChannelContext);
  const { users: channelUsers } = useChannelUsers({ channelId: channel?.id });
  const { users } = useResolveAssignees(value, channelUsers);
  const renderLeft = useMemo(
    () => (users.length === 0 ? undefined : () => <MBAssigneeUserIcon users={users} />),
    [users],
  );

  return (
    <MBTouchableOpacity activeOpacity={1} onPress={onPress} disabled={!onPress}>
      <MBFieldBlock
        value={users}
        presenter={fieldPresenter}
        block={block}
        renderLeft={renderLeft}
      />
    </MBTouchableOpacity>
  );
}
